import React, { useState, useContext, useMemo } from 'react'
import {
	Alert,
	Button,
	Col,
	Modal,
	ModalBody,
	Row
} from 'reactstrap'
import { SpinnerComponent } from 'react-element-spinner'

import { SessionContext } from '../../contexts/SessionProvider'
import AuctionTabsService from '../../services/AuctionTabsService'
import { trigger } from '../../events/customEvents'

/**
 * ReleaseToAuctionModal
 * 
 * BRD: "Ability to release units into live auction (individually or bulk release)"
 * BRD: "System should not allow release of a unit if prices are not placed in all three sections"
 * 
 * This modal confirms releasing a vehicle from "Awaiting Seller Release" status to "Live" auction.
 * Shows validation warnings if required pricing is missing.
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Object} auction - The auction object to release
 * @param {Function} onClose - Callback when modal is closed without action
 * @param {Function} onSuccess - Callback when release is successful
 */
const ReleaseToAuctionModal = ({ isOpen, auction, onClose, onSuccess }) =>
{
	const sessionContext = useContext(SessionContext)
	const [isBusy, setIsBusy] = useState(false)

	const getVin = () => auction?.vin || auction?.serialNumber || auction?.auctionVehicle?.vehicle?.serialNumber || 'Unknown VIN'

	/**
	 * BRD: Validate all three pricing fields are set before release
	 * - MBP Price (Market Based Price)
	 * - Start Price
	 * - Reserve Price
	 */
	const pricingValidation = useMemo(() => {
		if (!auction) return { isValid: true, missingPrices: [] }

		const missingPrices = []
		
		// Check MBP Price (on auction object)
		const mbpPrice = auction.mbpPrice ?? auction.MBPPrice ?? auction.marketPrice
		if (mbpPrice == null || mbpPrice <= 0) {
			missingPrices.push('MBP Price')
		}
		
		// Check Start Price
		const startPrice = auction.startPrice ?? auction.StartPrice
		if (startPrice == null || startPrice <= 0) {
			missingPrices.push('Start Price')
		}
		
		// Check Reserve Price
		const reservePrice = auction.reservePrice ?? auction.ReservePrice
		if (reservePrice == null || reservePrice <= 0) {
			missingPrices.push('Reserve Price')
		}

		return {
			isValid: missingPrices.length === 0,
			missingPrices
		}
	}, [auction])

	const handleRelease = () =>
	{
		setIsBusy(true)
		AuctionTabsService
			.releaseToAuction(auction.auctionId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				trigger('raiseNotification', {
					title: 'Release Queued',
					message: `VIN ${getVin()} has been queued for release. The auction will go live shortly.`,
					type: 'success',
					duration: 5
				})
				onSuccess?.()
			})
			.catch(error =>
			{
				setIsBusy(false)
				console.error('Error releasing to auction:', error)
				
				// Extract error message from various possible response formats
				let errorMessage = 'Failed to release vehicle to auction.'
				if (error?.response?.data?.message) {
					errorMessage = error.response.data.message
				} else if (error?.response?.data?.Message) {
					errorMessage = error.response.data.Message
				} else if (error?.response?.data?.title) {
					errorMessage = error.response.data.title
				} else if (error?.response?.data?.errors) {
					// Handle validation errors array
					const errors = Object.values(error.response.data.errors).flat()
					errorMessage = errors.join(', ')
				} else if (typeof error?.response?.data === 'string') {
					errorMessage = error.response.data
				} else if (error?.message) {
					errorMessage = error.message
				}
				
				trigger('raiseNotification', {
					title: 'Release Failed',
					message: errorMessage,
					type: 'danger',
					duration: 8
				})
			})
	}

	if (!auction) return null

	return (
		<Modal isOpen={isOpen} toggle={onClose} size="md">
			<SpinnerComponent
				loading={isBusy}
				position="global"
				color="#3578e5"
				spinnerType="circle-dots"
			/>
			<ModalBody>
				{/* BRD: Title shows VIN */}
				<h5 className="mb-3">VIN: {getVin()}</h5>
				
				{/* BRD: Show warning if pricing is missing */}
				{!pricingValidation.isValid && (
					<Alert color="warning" className="mb-3">
						<strong>Missing Required Pricing:</strong>
						<ul className="mb-0 mt-1">
							{pricingValidation.missingPrices.map((price, idx) => (
								<li key={idx}>{price}</li>
							))}
						</ul>
						<small className="d-block mt-2">
							All three prices (MBP, Start, Reserve) must be set before releasing to auction.
						</small>
					</Alert>
				)}

				{pricingValidation.isValid ? (
					<>
						<p>Are you sure you want to release this vehicle to live auction?</p>
						<p className="text-muted small">
							The vehicle will become visible to buyers and bidding can begin based on the configured iteration schedule.
						</p>
					</>
				) : (
					<p className="text-muted">
						Please set the missing prices before releasing this vehicle to auction.
					</p>
				)}

				<Row className="mt-4">
					<Col className="d-flex justify-content-end gap-2">
						<Button 
							color="primary" 
							onClick={handleRelease} 
							disabled={isBusy || !pricingValidation.isValid}
						>
							YES
						</Button>
						<Button color="secondary" outline onClick={onClose} disabled={isBusy}>
							Cancel
						</Button>
					</Col>
				</Row>
			</ModalBody>
		</Modal>
	)
}

export default ReleaseToAuctionModal
